import React, { useEffect, useState } from "react";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";

// Remote media URLs already fully loaded this session → render them directly
// (no local-preview swap) so re-renders / the 15s refresh never re-flicker.
const _loadedMedia = new Set();

// Returns the URL to display: keeps showing the local (blob) preview until the
// remote URL is fully downloaded in the background, then swaps to it invisibly.
// This kills the "image disappears for a second" flicker when an optimistic
// bubble (blob:) is reconciled to its server URL (https:).
export function useDisplaySrc(remoteRaw, localUrl, isVideo) {
  const remote = remoteRaw ? resolveMediaUrl(remoteRaw) : null;
  const hasLocal = !!localUrl && localUrl !== remote;
  const [ready, setReady] = useState(() => !hasLocal || !remote || _loadedMedia.has(remote));
  useEffect(() => {
    if (ready || !remote) return undefined;
    let alive = true;
    const done = () => { if (alive) { _loadedMedia.add(remote); setReady(true); } };
    const fail = () => { if (alive) setReady(true); };
    if (isVideo) {
      const v = document.createElement("video");
      v.preload = "metadata"; v.muted = true;
      v.onloadeddata = done; v.onloadedmetadata = done; v.onerror = fail;
      v.src = `${remote}#t=0.1`;
      return () => { alive = false; };
    }
    const img = new Image();
    img.onload = done; img.onerror = fail;
    img.src = remote;
    return () => { alive = false; };
  }, [remote, ready, isVideo]);
  return (!ready && hasLocal) ? localUrl : (remote || localUrl);
}

export const SmartImg = ({ remoteUrl, localUrl, ...rest }) => {
  const src = useDisplaySrc(remoteUrl, localUrl, false);
  return <img src={src} alt="" {...rest} />;
};

export const SmartVideo = ({ remoteUrl, localUrl, className, poster, ...rest }) => {
  const src = useDisplaySrc(remoteUrl, localUrl, true);
  return <video src={`${src}#t=0.1`} muted playsInline preload="metadata" poster={poster || NEUTRAL_POSTER} className={className} {...rest} />;
};
